import React, { getGlobal } from 'reactn';
import { get } from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import { SegmentedControl } from '@mantine/core';
import Card from '../../common/components/Card';
import WeekPicker from '../../common/components/WeekPicker';
import TimesheetContainer from './TimesheetContainer';
import TimesheetUploadsContainer from './TimesheetUploadsContainer';
import { Query } from 'react-apollo';
import { GET_USER_TIMESHEET } from '../queries';
import Page from '../../common/components/Page';
import { getWeekEndDayForDate } from '../../common';

const Message = styled.div`
  color: red;
  font-size: 1.2em;
  margin-bottom: 1em;
`;

class TimesheetCard extends React.Component {
  state = {
    selectedView: 'Grid', // Set default view to 'Grid'
  };

  handleViewChange = value => {
    this.setState({ selectedView: value });
  };

  changeUrl = endDate => this.props.history.push(`/user/timesheets/${endDate}`);

  componentDidMount() {
    const paramsEndDate = get(this.props, 'match.params.endDate');
    
    if (!paramsEndDate) {
      return;
    }

    // Get the current date from paramsEndDate
    const currentDate = moment(paramsEndDate, 'YYYYMMDD');
    if (!currentDate.isValid()) {
      return;
    }

    const currentDayOfWeek = currentDate.day(); // 0 = Sunday, 6 = Saturday
    
    // Use getWeekEndDayForDate to respect historical week-end-day config changes
    // This ensures dates before a config change use the previous week-end-day value
    const weekEndDay = getWeekEndDayForDate(currentDate);
    const dayMapping = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const targetDayOfWeek = dayMapping[weekEndDay];

    // If the date matches current config's weekEndDay, it's valid - don't redirect
    if (currentDayOfWeek === targetDayOfWeek) {
      return;
    }

    // For dates that don't match current config, check if they're in the past
    // If in the past, they might be historical timesheets - let them load first
    // Only redirect future dates or dates that are clearly not week-end-days
    const isPastDate = currentDate.isBefore(moment(), 'day');
    
    if (isPastDate) {
      // For past dates, allow them to load - they might be historical timesheets
      // The Query will load the timesheet and we can validate based on its weekEndDay
      return;
    }

    // For future dates that don't match current config, redirect to correct week end date
    // This ensures new timesheets use the current week-end-day config
    let daysToAdd = (targetDayOfWeek - currentDayOfWeek + 7) % 7;
    if (daysToAdd === 0) {
      daysToAdd = 7; // Move to the next week if it's the same day
    }

    // Calculate the next end date
    const nextEndDate = currentDate.clone().add(daysToAdd, 'days');
    this.props.changeTimesheetDate(nextEndDate.format('YYYYMMDD'));
    this.props.history.replace(`/user/timesheets/${nextEndDate.format('YYYYMMDD')}`);
  }

  componentDidUpdate(prevProps) {
    // After timesheet loads, validate if needed
    const paramsEndDate = get(this.props, 'match.params.endDate');
    const prevEndDate = get(prevProps, 'match.params.endDate');
    
    // Only validate if endDate changed and we have timesheet data
    if (paramsEndDate !== prevEndDate) {
      // The Query will handle loading the timesheet
      // If it's a historical timesheet with different weekEndDay, it will load correctly
      // No additional validation needed here since we allow past dates in componentDidMount
    }
  }

  render() {
    const { selectedView } = this.state;
    const showAttachments = this.global?.config['timesheet-attachments'] === '1';
    const global = getGlobal();
    const endDate = get(this.props, 'match.params.endDate');

    return (
      <Page full={selectedView !== 'Grid'}>
        <Query query={GET_USER_TIMESHEET} variables={{ endDate, user: null }}>
          {({ loading, error, data }) => {
            if (error) {
              window.location.reload();
            }
            return (
              <>
                {global?.config['company-short-name'] === 'Spruce' &&
                  moment(get(this.props, 'match.params.endDate')).isBefore(
                    moment('2023-09-18'),
                  ) && (
                    <Message>
                      <strong>ATTENTION:</strong> This timesheet system will only accept time
                      entries starting on 9/18/2023. If you need to submit time for a date prior,
                      please use{' '}
                      <a href={`https://portal.sprucetech.com/user/timesheets`}>
                        https://portal.sprucetech.com
                      </a>
                      .
                    </Message>
                  )}
                <Card
                  border
                  title="Weekly Timesheet"
                  padded={false}
                  floating
                  loading={data?._id || loading}
                  withShadow
                  actionComponent={
                    <div className="flex flex-col md:flex-row md:items-center gap-2">
                      <div className="hidden md:block">
                        <SegmentedControl
                          data={['Grid', 'Blocks', 'Calendar']}
                          value={selectedView}
                          onChange={this.handleViewChange}
                        />
                      </div>
                      <WeekPicker
                        endDate={endDate}
                        onDateChange={endDate => {
                          const endDateString = moment(endDate).format('YYYYMMDD');
                          this.changeUrl(endDateString);
                          this.props.changeTimesheetDate(endDateString);
                        }}
                      />
                    </div>
                  }
                  style={{ minWidth: '1000px' }}
                >
                  <TimesheetContainer
                    timesheet={data?.timesheet}
                    endDate={endDate}
                    view={selectedView}
                  />
                </Card>
                {showAttachments && !loading && data?.timesheet && (
                  <Card title="Attachments" border padded={false} floating withShadow>
                    <TimesheetUploadsContainer
                      readonly={['Submitted-Pending', 'Approved'].includes(data?.timesheet.status)}
                      timesheet={data?.timesheet}
                      endDate={endDate}
                    />
                  </Card>
                )}
              </>
            );
          }}
        </Query>
      </Page>
    );
  }
}

export default TimesheetCard;
